import React, { useState, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import ServiceRequestProgressFlow from '../../components/ServiceRequestProgressFlow';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useAuth } from '../../contexts/AuthContext';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { ASSIGNMENT_ONLY_LOCK_CLASS } from '../../utils/ticketAssignmentEdit';
import RichTextEditor from '../../components/RichTextEditor';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';
import { trackServiceRequestOperation } from '../../utils/appInsights';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface ServiceRequestEditForm {
  shortDescription: string;
  description: string;
  impact: string;
  urgency: string;
  priority: string;
  customerId?: string;
  engagementId?: string;
  servicePlanId?: string;
  resourceId?: string;
  resourceIdValue?: string;
  assignmentGroupId?: string;
  assignedToId?: string;
  contactId?: string;
}

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning'
};

// Priority calculation function (matches backend logic)
const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
  if (!impact || !urgency) return 'low_4'; // Default to Low

  const impactNum = parseInt(impact.split('_')[1]);
  const urgencyNum = parseInt(urgency.split('_')[1]);

  if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
  if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
  if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
  if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
  if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

  return 'low_4'; // Default to Low
};

const ServiceRequestEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasPermission = can('service_requests', 'update');
  const [isSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [contactQuery, setContactQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const { formatDateTime } = useDateFormatter();

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingData: ServiceRequestEditForm;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingData: {} as ServiceRequestEditForm
  });

  const [formData, setFormData] = useState<ServiceRequestEditForm>({
    shortDescription: '',
    description: '',
    impact: 'low_3',
    urgency: 'low_3',
    priority: 'planning_5',
    customerId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    resourceIdValue: '',
    assignmentGroupId: '',
    assignedToId: '',
    contactId: ''
  });

  // Fetch existing service request data
  const { data: existingData, isLoading } = useQuery({
    queryKey: ['service-request', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data;
    },
    enabled: !!id
  });

  const { isAssignmentOnlyEditMode } = useTicketPermissions(existingData, 'cases');

  // Fetch customers
  // Note: Customer and Engagement are now readonly fields in edit mode
  // They are displayed from existingData, not from these queries

  // Fetch service plans based on selected engagement
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', formData.engagementId || existingData?.engagementId],
    queryFn: async () => {
      const engId = formData.engagementId || existingData?.engagementId;
      if (!engId) return { servicePlans: [] };
      const response = await api.get(`/engagements/${engId}/service-plans`);
      return response.data;
    },
    enabled: !!(formData.engagementId || existingData?.engagementId)
  });

  // Fetch assignment groups
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      try {
        return await fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch users - get ALL users for dropdown (no pagination)
  const { data: usersData } = useQuery({
    queryKey: ['users-all'],
    queryFn: async () => {
      const response = await api.get('/users?limit=1000&active=true');
      return response.data;
    }
  });

  // No need to fetch user's groups - we'll check membership when needed using group members

  // Fetch contacts for Contact dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId, contactQuery],
    queryFn: async () => {
      if (!formData.customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        limit: '100'
      });
      if (contactQuery) {
        params.append('search', contactQuery);
      }
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Memoize arrays to prevent unnecessary re-renders
  const assignmentGroups = React.useMemo(() => assignmentGroupsData || [], [assignmentGroupsData]);
  const users = React.useMemo(() => usersData?.users || [], [usersData]);

  // Filter users based on selected assignment group - use group members directly
  const filteredUsers = React.useMemo(() => {
    // If assignment group is selected, return only its members
    if (formData.assignmentGroupId && groupMembersData && groupMembersData.length > 0) {
      return groupMembersData;
    }
    // Otherwise show all users
    return users || [];
  }, [formData.assignmentGroupId, groupMembersData, users]);

  // Filtered data for comboboxes
  const filteredAssignmentGroups = assignmentGroupQuery === ''
    ? assignmentGroups
    : assignmentGroups.filter((g: any) =>
        g.name?.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
      );

  const filteredAssignees = assignedToQuery === ''
    ? filteredUsers
    : filteredUsers.filter((u: any) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase())
      );

  // Track if form has been initialized from existing data
  const [formInitialized, setFormInitialized] = React.useState(false);

  // Initialize form data from existing data
  React.useEffect(() => {
    if (existingData) {
      setFormData({
        shortDescription: existingData.shortDescription || '',
        description: existingData.description || '',
        impact: existingData.impact || 'low_3',
        urgency: existingData.urgency || 'low_3',
        priority: existingData.priority || 'planning_5',
        customerId: existingData.customerId || '',
        engagementId: existingData.engagementId || '',
        servicePlanId: existingData.servicePlanId || '',
        resourceId: existingData.resourceId || '',
        resourceIdValue: existingData.resourceIdValue || '',
        assignmentGroupId: existingData.assignmentGroupId || '',
        assignedToId: existingData.assignedToId || '',
        contactId: existingData.contactId || ''
      });
      setFormInitialized(true);
    }
  }, [existingData]);

  // Mutation for updating service request
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: (updated: any) => {
      // Check if this was a confirmed assignment group change - if so, redirect to list
      if (groupChangeConfirmModal.isOpen) {
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingData: {} as ServiceRequestEditForm
        });
        toast.success('Service request updated successfully. You no longer have access to this service request.');
        queryClient.invalidateQueries({ queryKey: ['cases'] });
        navigate('/service-requests');
        return;
      }

      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['service-request', id] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });

      toast.success('Service request updated successfully');
      
      // Log service request details modification based on active tab
      if (user && existingData) {
        const serviceRequestNumber = existingData.number || existingData.id || id || 'Unknown';
        let action = 'Service Request Details Modified';
        
        // Determine action based on active tab (for future tabs like schedule, closure)
        if (activeTab === 'details') {
          action = 'Service Request Details Modified';
        }
        // Add more conditions for other tabs when they're implemented
        
        trackServiceRequestOperation(
          'Service Request Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: action,
            serviceRequestNumber: serviceRequestNumber,
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      navigate(`/service-requests/${id}`);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update service request';
      toast.error(errorMessage);
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    // When assignment group changes (e.g. L1 assigns to L2 group), always clear Assigned To
    // so the field is blank and L2 can assign to one of their members.
    if (name === 'assignmentGroupId' && value && value !== formData.assignmentGroupId) {
      setFormData(prev => ({
        ...prev,
        assignmentGroupId: value,
        assignedToId: ''
      }));
      return;
    }

    // Auto-calculate priority when impact or urgency changes
    if (name === 'impact') {
      const newPriority = calculatePriorityFromImpactUrgency(value, formData.urgency);
      setFormData(prev => ({
        ...prev,
        impact: value,
        priority: newPriority
      }));
      return;
    }

    if (name === 'urgency') {
      const newPriority = calculatePriorityFromImpactUrgency(formData.impact, value);
      setFormData(prev => ({
        ...prev,
        urgency: value,
        priority: newPriority
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isAssignmentOnlyEditMode) {
      if (!formData.assignmentGroupId) {
        toast.error('Assignment Group is required');
        return;
      }

      updateMutation.mutate({
        assignmentGroupId: formData.assignmentGroupId,
        assignedTo: formData.assignedToId || null,
      });
      return;
    }

    // Validate required fields
    if (!formData.shortDescription || formData.shortDescription.trim() === '' || formData.shortDescription === '<p></p>') {
      toast.error('Short Description is required');
      return;
    }
    if (!formData.assignmentGroupId) {
      toast.error('Assignment Group is required');
      return;
    }

    // Prepare update payload
    const updateData = {
      shortDescription: formData.shortDescription,
      description: formData.description || null,
      impact: formData.impact,
      urgency: formData.urgency,
      priority: formData.priority,
      customerId: formData.customerId,
      engagementId: formData.engagementId,
      servicePlanId: formData.servicePlanId || null,
      assignmentGroupId: formData.assignmentGroupId,
      assignedTo: formData.assignedToId || null,
      contactId: formData.contactId || null
    };

    // Check if assignment group is changing and user is not a member of the new group
    // Only check for regular users (user/agent roles), not for super_admin/admin/manager
    // Service Desk users can access all tickets, so skip the warning for them
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    const originalAssignmentGroupId = existingData?.assignmentGroupId;
    if (updateData.assignmentGroupId && updateData.assignmentGroupId !== originalAssignmentGroupId && !isGlobalAccessRole && !isServiceDeskUser) {
      const newGroupId = updateData.assignmentGroupId;

      // Check if user is a member of the new group by checking group members
      // groupMembersData is already fetched for the selected assignment group
      const newGroupMembers = groupMembersData || [];
      const isUserMemberOfNewGroup = newGroupMembers.some((member: any) =>
        member.id === user?.id || member._id === user?.id
      );

      if (!isUserMemberOfNewGroup) {
        // Find the new group name
        const newGroup = assignmentGroups.find((group: any) =>
          group.id === newGroupId || group._id === newGroupId
        );
        const newGroupName = newGroup?.name || 'Unknown Group';

        // Show confirmation modal instead of saving immediately
        setGroupChangeConfirmModal({
          isOpen: true,
          newGroupId,
          newGroupName,
          pendingData: formData
        });
        return;
      }
    }

    updateMutation.mutate(updateData);
  };

  // Handler for confirming assignment group change
  const handleConfirmGroupChange = () => {
    // Save the changes and redirect to list after successful save
    const updateData = {
      shortDescription: groupChangeConfirmModal.pendingData.shortDescription,
      description: groupChangeConfirmModal.pendingData.description || null,
      impact: groupChangeConfirmModal.pendingData.impact,
      urgency: groupChangeConfirmModal.pendingData.urgency,
      priority: groupChangeConfirmModal.pendingData.priority,
      customerId: groupChangeConfirmModal.pendingData.customerId,
      engagementId: groupChangeConfirmModal.pendingData.engagementId,
      servicePlanId: groupChangeConfirmModal.pendingData.servicePlanId || null,
      assignmentGroupId: groupChangeConfirmModal.pendingData.assignmentGroupId,
      assignedTo: groupChangeConfirmModal.pendingData.assignedToId || null,
      contactId: groupChangeConfirmModal.pendingData.contactId || null
    };
    updateMutation.mutate(updateData);
    // Note: Navigation to list will happen in mutation's onSuccess
  };

  // Handler for cancelling assignment group change
  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingData: {} as ServiceRequestEditForm
    });
  };

  const getPriorityDisplay = (priority: string) => {
    const label = priorityLabels[priority];
    const match = priority?.match(/_(\d)/);
    const number = match ? match[1] : priority;
    return label ? `${number} - ${label}` : priority || '-';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading service request...</div>
      </div>
    );
  }

  if (!existingData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Service request not found</p>
        </div>
      </div>
    );
  }

  // Check if the service request is closed
  const isClosedState = existingData.state === 'closed' ||
                       existingData.state === 'cancelled';

  if (isClosedState) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                This service request is closed and cannot be edited
              </h3>
              <div className="mt-2">
                <button
                  onClick={() => navigate(`/service-requests/${id}`)}
                  className="text-sm font-medium text-yellow-800 hover:text-yellow-700 underline"
                >
                  Return to service request details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to update service-requests.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/service-requests')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Service Requests
        </button>
      </div>

      {/* Show info if Service Request was converted from case and doesn't have an assigned user */}
      {existingData.convertedFromCase && !existingData.assignedToId && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong className="font-medium">Note:</strong> This Service Request was created from a Case. You may optionally assign it to a user below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Flow */}
      <ServiceRequestProgressFlow
        currentState={existingData.state || 'new'}
      />

      {/* Main Content */}
      <div className="bg-white shadow rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{existingData.number}</h1>
              <p className="mt-1 text-sm text-gray-500">
                Created on {formatDateTime(existingData.createdAt)}
              </p>
              {existingData.shortDescription && (
                <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                  {stripHtml(existingData.shortDescription)}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                formData.priority === 'critical_1' ? 'bg-red-100 text-red-800 border-red-200' :
                formData.priority === 'high_2' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                formData.priority === 'moderate_3' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                formData.priority === 'low_4' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                'bg-gray-100 text-gray-800 border-gray-200'
              }`}>
                Priority {getPriorityDisplay(formData.priority)}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            <button
              onClick={() => setActiveTab('approvals')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'approvals'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Approvals
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {isAssignmentOnlyEditMode && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  You can update Assignment Group and Assigned To for this ticket. Other fields are read-only.
                </div>
              )}
              <div className={isAssignmentOnlyEditMode ? `${ASSIGNMENT_ONLY_LOCK_CLASS} space-y-6` : 'space-y-6'}>
              {/* Number and State */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.number || ''}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.state || 'new'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact and Urgency */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Impact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <select
                        name="impact"
                        value={formData.impact}
                        onChange={handleChange}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        <option value="high_1">1 - High</option>
                        <option value="medium_2">2 - Medium</option>
                        <option value="low_3">3 - Low</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Urgency
                      </label>
                    </div>
                    <div className="col-span-9">
                      <select
                        name="urgency"
                        value={formData.urgency}
                        onChange={handleChange}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        <option value="high_1">1 - High</option>
                        <option value="medium_2">2 - Medium</option>
                        <option value="low_3">3 - Low</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Priority */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={getPriorityDisplay(formData.priority)}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer and Engagement */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.Customer?.accountName || '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Engagement
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.Engagement ? `${existingData.Engagement.name}${existingData.Engagement.status ? ` - ${existingData.Engagement.status}` : ''}` : '-'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact and Service Plan */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.contactId || ''}
                        onChange={(value) => setFormData({ ...formData, contactId: value || '' })}
                        disabled={!formData.customerId}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                              !formData.customerId ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            displayValue={(userId: string) => {
                              const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                              return user ? `${user.firstName} ${user.lastName}` : '';
                            }}
                            onChange={(event) => setContactQuery(event.target.value)}
                            placeholder={!formData.customerId ? 'Select Customer first' : 'Search contacts...'}
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setContactQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {contactsData?.users && contactsData.users.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No contacts available.
                                </div>
                              ) : (
                                (contactsData?.users || []).map((user: any) => (
                                  <Combobox.Option
                                    key={user.id}
                                    value={user.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {user.firstName} {user.lastName} ({user.email})
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Plan
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.servicePlanId || ''}
                        onChange={(value) => setFormData({ ...formData, servicePlanId: value || '' })}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(servicePlanId: string) => {
                              const plan = (servicePlansData?.servicePlans || servicePlansData?.plans || []).find((p: any) => p.id === servicePlanId);
                              return plan ? plan.name : '';
                            }}
                            onChange={(e) => setServicePlanQuery(e.target.value)}
                            placeholder="Select Service Plan"
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setServicePlanQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {(servicePlansData?.servicePlans || servicePlansData?.plans || [])
                                .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                                .map((plan: any) => (
                                  <Combobox.Option
                                    key={plan.id}
                                    value={plan.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                          {plan.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}>
                                            <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resource and Resource ID */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Resource
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.otherResourceNames
                          ? 'Others (Custom)'
                          : (existingData.Resource?.name || '-')}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Resource ID
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={existingData.otherResourceNames
                          ? existingData.otherResourceNames
                          : (existingData.Resource?.resourceId || existingData.resourceIdValue || '-')}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              </div>

              {/* Assignment Group and Assigned To */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assignment Group <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.assignmentGroupId || ''}
                        onChange={(value) => {
                          // When assignment group changes (e.g. L1 assigns to L2), always clear Assigned To
                          if (value && value !== formData.assignmentGroupId) {
                            setFormData(prev => ({
                              ...prev,
                              assignmentGroupId: value || '',
                              assignedToId: ''
                            }));
                            return;
                          }
                          setFormData(prev => ({ ...prev, assignmentGroupId: value || '' }));
                        }}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            displayValue={(groupId: string) => {
                              const group = assignmentGroups.find((g: any) => g.id === groupId);
                              return group ? group.name : '';
                            }}
                            onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                            placeholder="Search assignment groups..."
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setAssignmentGroupQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {filteredAssignmentGroups.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No groups found.
                                </div>
                              ) : (
                                filteredAssignmentGroups.map((group: any) => (
                                  <Combobox.Option
                                    key={group.id}
                                    value={group.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {group.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                    </div>
                    <div className="col-span-9">
                      <Combobox
                        value={formData.assignedToId || ''}
                        onChange={(value) => setFormData(prev => ({ ...prev, assignedToId: value || '' }))}
                        disabled={!formData.assignmentGroupId}
                      >
                        <div className="relative">
                          <Combobox.Input
                            className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                              !formData.assignmentGroupId
                                ? 'bg-gray-100 cursor-not-allowed border-gray-300'
                                : 'border-gray-300 focus:ring-blue-400'
                            }`}
                            displayValue={(userId: string) => {
                              const user = filteredUsers.find((u: any) => u.id === userId);
                              return user ? `${user.firstName} ${user.lastName}` : '';
                            }}
                            onChange={(event) => setAssignedToQuery(event.target.value)}
                            placeholder={!formData.assignmentGroupId ? 'Select Assignment Group first' : 'Search assignees...'}
                          />
                          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                          </Combobox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            afterLeave={() => setAssignedToQuery('')}
                          >
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                              {filteredAssignees.length === 0 ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                  No members found.
                                </div>
                              ) : (
                                filteredAssignees.map((user: any) => (
                                  <Combobox.Option
                                    key={user.id}
                                    value={user.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {user.firstName} {user.lastName}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))
                              )}
                            </Combobox.Options>
                          </Transition>
                        </div>
                      </Combobox>
                      {!formData.assignmentGroupId && (
                        <p className="mt-1 text-xs text-gray-500">
                          Select an assignment group first
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={isAssignmentOnlyEditMode ? `${ASSIGNMENT_ONLY_LOCK_CLASS} space-y-6` : 'space-y-6'}>
              {/* Short Description */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Short Description <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-10">
                      {formInitialized ? (
                        <RichTextEditor
                          key={`short-desc-${existingData?.id}`}
                          value={formData.shortDescription}
                          onChange={(value) => setFormData(prev => ({ ...prev, shortDescription: value }))}
                          placeholder="Brief summary of the service request"
                          minHeight="120px"
                        />
                      ) : (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-400 text-sm min-h-[120px]">
                          Loading...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                    </div>
                    <div className="col-span-10">
                      {formInitialized ? (
                        <RichTextEditor
                          key={`desc-${existingData?.id}`}
                          value={formData.description}
                          onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                          placeholder="Detailed description of the service request"
                          minHeight="180px"
                        />
                      ) : (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-400 text-sm min-h-[180px]">
                          Loading...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => navigate(`/service-requests/${id}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="text-gray-500 text-sm">
              Notes functionality will be available in the next update
            </div>
          )}

          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <div className="text-gray-500 text-sm">
              Approval management will be available in the next update
            </div>
          )}
        </form>
      </div>

      {/* Assignment Group Change Confirmation Modal */}
      <AssignmentGroupChangeConfirmModal
        isOpen={groupChangeConfirmModal.isOpen}
        onConfirm={handleConfirmGroupChange}
        onCancel={handleCancelGroupChange}
        newGroupName={groupChangeConfirmModal.newGroupName}
        ticketNumber={existingData?.number}
      />
    </div>
  );
};

export default ServiceRequestEdit;